/**
 * The versatility seam: maps a context_type to how its value is shaped on
 * the wire (an array of {type, question, question_type, answer, options}
 * nodes) so the backend, the propose_document_changes schema and the diff
 * UI never need to change to support a new context — only this map (and,
 * for a genuinely new shape, a new adapter mode) does.
 *
 * `structured` contexts (prontuário, anamnese) are already in the array
 * shape and pass through unchanged. `plain` and `html` contexts (session
 * annotations, a message template, a rich-text document) wrap the string
 * as a single synthetic question node here and unwrap it in
 * fromWireDocument — every other piece of the agent
 * (propose_document_changes, the diff UI) keeps working without
 * modification. `html` only differs from `plain` in that the diff card
 * renders before/after as rich text instead of escaped plain text.
 */
const CONTEXT_ADAPTERS = {
  customer_professional_record: { mode: 'structured' },
  customer_anamnese: { mode: 'structured' },
  session_annotations: { mode: 'plain', label: 'Anotações da sessão' },
  billing_message: { mode: 'plain', label: 'Mensagem' },
  document_content: { mode: 'html', label: 'Conteúdo do documento' },
}

const DEFAULT_ADAPTER = { mode: 'structured' }

function getAdapter(contextType) {
  return CONTEXT_ADAPTERS[contextType] || DEFAULT_ADAPTER
}

const IMG_TAG_RE = /<img[^>]*>/g

/**
 * Embedded images (AppEditor's images_upload_handler inlines uploads as
 * base64 data-URIs) blow past propose_document_changes' max_answer_length
 * on their own and have no reason to round-trip through the model at all.
 * Replaced with small positional placeholders before the document is sent,
 * and swapped back in restoreEmbeddedImages once a change is applied.
 *
 * @param {string} html
 * @returns {{text: string, images: string[]}}
 */
function stripEmbeddedImages(html) {
  const images = []

  const text = String(html || '').replace(IMG_TAG_RE, (match) => {
    images.push(match)
    return `[[IMG_${images.length - 1}]]`
  })

  return { text, images }
}

/**
 * @param {string} text
 * @param {string[]} images
 * @returns {string}
 */
function restoreEmbeddedImages(text, images) {
  let result = String(text || '')

  ;(images || []).forEach((img, index) => {
    result = result.split(`[[IMG_${index}]]`).join(img)
  })

  return result
}

/**
 * @param {*} value The value as the parent form holds it.
 * @param {string} contextType
 * @returns {{document: Array, images: string[]}} The array shape sent to
 *   the backend, plus any images stripped out of it (html mode only —
 *   restore them via fromWireDocument).
 */
export function toWireDocument(value, contextType) {
  const adapter = getAdapter(contextType)

  if (adapter.mode === 'plain' || adapter.mode === 'html') {
    // The call site defaults a missing document to `[]` regardless of mode
    // (it doesn't know which shape this context expects) — an empty array
    // is truthy, so `value || ''` would let it through as the "answer"
    // instead of normalizing to an empty string.
    const rawValue = typeof value === 'string' ? value : ''

    const { text, images } = adapter.mode === 'html'
      ? stripEmbeddedImages(rawValue)
      : { text: rawValue, images: [] }

    return {
      document: [{
        type: 'question',
        question: adapter.label || 'Texto',
        question_type: 'open',
        answer: text,
        html: adapter.mode === 'html',
      }],
      images,
    }
  }

  return { document: Array.isArray(value) ? value : [], images: [] }
}

/**
 * @param {Array} document The working document array.
 * @param {string} contextType
 * @param {string[]} images Images previously stripped by toWireDocument.
 * @returns {*} The value shape the parent form's @apply handler expects.
 */
export function fromWireDocument(document, contextType, images = []) {
  const adapter = getAdapter(contextType)

  if (adapter.mode === 'plain' || adapter.mode === 'html') {
    const node = (document || []).find((item) => item.type === 'question')
    const answer = node ? node.answer : ''
    return adapter.mode === 'html' ? restoreEmbeddedImages(answer, images) : answer
  }

  return document
}

/**
 * Applies one accepted change to the document. A regular change replaces
 * only the answer at `change.index`, preserving every other field of that
 * node. A `is_new` change (see ProposeDocumentChangesTool — only possible
 * for a growable context type) instead appends a brand-new question node
 * at the end — there is no existing index to replace.
 *
 * @param {Array} document
 * @param {{index: number|null, is_new?: boolean, question?: string, after: string, html?: boolean}} change
 * @returns {Array} A new document array with the change applied.
 */
export function applyChange(document, change) {
  if (change.is_new) {
    return [
      ...document,
      {
        type: 'question',
        question: change.question,
        question_type: 'open',
        answer: change.after,
        html: !!change.html,
      },
    ]
  }

  return document.map((node, index) => {
    if (index !== change.index) {
      return node
    }

    return { ...node, answer: change.after }
  })
}

/**
 * Removes the node at `index` — used to undo an accepted `is_new` change,
 * since its `before` is empty and simply reverting the answer would leave
 * an orphan blank node instead of removing the block that was added.
 *
 * @param {Array} document
 * @param {number} index
 * @returns {Array}
 */
export function removeNodeAt(document, index) {
  return document.filter((_, i) => i !== index)
}
